import React, { Component, ErrorInfo } from 'react';

import { AnalyticsClient, EventAttributes } from '../utilities/analytics/analytics-web-client';
import { BaseErrorPage as ErrorBoundaryErrorPage } from './ErrorPage';
import { FormattedMessage } from 'react-intl';
import {
  captureMalformedError,
  captureTaggedReactException,
} from '../utilities/analytics/error-reporting';
import { Capability } from '../utilities/analytics/capability-tags';
import { FetchTimeoutError } from '../utilities/fetch-api';

interface Props {
  children: React.ReactNode;
  analyticsClient: AnalyticsClient;
  // TODO: make the capability prop non-optional KIRBY-9124
  capabilities?: Capability[];
  skipAnalytics?: boolean;
}

interface State {
  errorType?: 'network' | 'unexpected';
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {};
  }

  componentDidCatch(error: Error | unknown, errorInfo: ErrorInfo) {
    // Handle case where the error is not specific enough for SLO but we still want to know about it
    // Example: build tools result in Event being thrown instead of Error, for failures to load
    // dynamically imported modules - should not burn SLO but is worth alerting via Sentry.
    if (!(error instanceof Error)) {
      this.setState({ errorType: 'unexpected' });

      captureMalformedError(
        new Error(`Caught object that is not of type Error: ${error}`),
        { error },
        'not-an-instance-of-error'
      );

      return;
    }

    // Thrown when all retry attempts are exhausted by fetchApiWithNetworkRetry
    if (error instanceof FetchTimeoutError) {
      this.setState({ errorType: 'network' });
      return;
    }

    // Failed to fetch dynamically imported module:
    // Importing a module script failed.
    // error loading dynamically imported module:
    // Cannot find module
    if (/(import|find).*.module/i.test(error.message)) {
      this.setState({ errorType: 'network' });
      return;
    }

    this.setState({ errorType: 'unexpected' });

    if (this.props.skipAnalytics) {
      return;
    }

    const page = window.location.pathname;

    const attributes: EventAttributes = {
      errorName: error.name,
      errorMessage: error.message,
      errorComponentStack: errorInfo.componentStack || undefined,
      errorStack: error.stack,
    };

    captureTaggedReactException(error, errorInfo, {
      capabilities: this.props.capabilities || [],
      extraProperties: {
        'ErrorBoundary.action': 'loadPageFailure',
        'ErrorBoundary.pageId': page,
      },
    });

    this.props.analyticsClient.operationalEvent({
      page: page,
      action: 'loadPageFailure',
      subject: 'sli',
      attributes,
    });
  }

  render() {
    const { errorType } = this.state;
    const anonymousId = this.props.analyticsClient.getAnonymousId();

    if (errorType === 'network') {
      return (
        <ErrorBoundaryErrorPage
          title={
            <FormattedMessage
              id="error.boundary.error.title"
              defaultMessage="Something went wrong"
            />
          }
          anonymousId={anonymousId}>
          <FormattedMessage
            id="error.boundary.network.error"
            defaultMessage="We were unable to load this page, possibly due to a network connectivity issue. Please check your network connection and try again."
            tagName="p"
          />
        </ErrorBoundaryErrorPage>
      );
    }

    if (errorType === 'unexpected') {
      return <UnexpectedApplicationErrorPage anonymousId={anonymousId} />;
    }

    return this.props.children;
  }
}

export const UnexpectedApplicationErrorPage = ({ anonymousId }: { anonymousId?: string }) => (
  <ErrorBoundaryErrorPage
    title={
      <FormattedMessage id="error.boundary.error.title" defaultMessage="Something went wrong" />
    }
    anonymousId={anonymousId}>
    <FormattedMessage
      id="error.boundary.unexpected.error"
      defaultMessage="An unexpected error occurred. We are looking into it. Please try again later."
      tagName="p"
    />
  </ErrorBoundaryErrorPage>
);
